import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    LinearProgress,
    Paper,
    Stack,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { db } from '../firebase/firebaseConfig';
import { useSelector } from 'react-redux';
import { useAdminStaffData } from '../hooks/useAdminStaffData';
import { parseExcelFile, processImport, MAX_ROWS } from '../utils/excelImport';

const ImportPoliciesDialog = ({ open, onClose, onSuccess }) => {
    const { user } = useSelector((state) => state.auth);
    const { isAdmin } = useAdminStaffData();

    // State
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [progressStatus, setProgressStatus] = useState('');
    const [progressCurrent, setProgressCurrent] = useState(0);
    const [progressTotal, setProgressTotal] = useState(0);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const handleClose = () => {
        if (loading) return; // prevent closing during import
        if (result) {
            onSuccess?.();
        }
        setFile(null);
        setLoading(false);
        setProgressStatus('');
        setProgressCurrent(0);
        setProgressTotal(0);
        setResult(null);
        setError(null);
        onClose();
    };

    const handleFileSelect = (event) => {
        const selected = event.target.files?.[0];
        processSelectedFile(selected);
    };

    const processSelectedFile = (selectedFile) => {
        if (!selectedFile) return;
        const ext = selectedFile.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls'].includes(ext)) {
            setError('Please select an Excel file (.xlsx or .xls)');
            return;
        }
        setFile(selectedFile);
        setError(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processSelectedFile(files[0]);
        }
    };

    const handleSubmit = async () => {
        if (!file) return;

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            // Step 1: Parse
            setProgressStatus('Uploading file');
            const { rows, headers } = await parseExcelFile(file);

            if (!rows || rows.length === 0) {
                throw new Error('No data found in the Excel file');
            }

            if (rows.length > MAX_ROWS) {
                throw new Error(`File contains ${rows.length} rows. Maximum allowed is ${MAX_ROWS} rows per upload.`);
            }

            setProgressTotal(rows.length);

            // Step 2: Process (mapping + record creation)
            const agentId = user?.uid;
            if (!agentId) throw new Error('User not authenticated');

            const onProgress = (status, current, total) => {
                setProgressStatus(status);
                setProgressCurrent(current);
                setProgressTotal(total);
            };

            const importResult = await processImport(rows, headers, agentId, db, onProgress);

            setResult(importResult);
        } catch (err) {
            console.error('Import error:', err);
            setError(err.message || 'Import failed');
        } finally {
            setLoading(false);
        }
    };

    const progressPercent = progressTotal > 0 ? Math.round((progressCurrent / progressTotal) * 100) : 0;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(2px)',
                    }
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" component="div" sx={{ fontFamily: 'Roboto Slab', fontWeight: 500 }}>
                    Import Policies from Excel
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ textAlign: 'center', pt: 1 }}>
                {/* ---- FORM STATE (no result, not loading) ---- */}
                {!result && !loading && (
                    <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* File Upload */}
                        <Box
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            sx={{
                                border: isDragging ? '2px dashed #0063F7' : '2px dashed #ccc',
                                borderRadius: '4px',
                                p: 4,
                                backgroundColor: isDragging ? '#F0F7FF' : '#FAFAFA',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 180,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': { backgroundColor: '#F0F5FF', borderColor: '#0063F7' }
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <UploadFileIcon sx={{ fontSize: 40, color: '#757575', mb: 1 }} />
                            <Typography
                                sx={{
                                    fontFamily: 'Roboto',
                                    fontWeight: 400,
                                    fontSize: '22px',
                                    lineHeight: '30px',
                                    mb: 1,
                                    textAlign: 'center'
                                }}
                            >
                                Drag and drop or click to upload your Excel file
                            </Typography>
                            <Button
                                sx={{
                                    color: '#0063F7',
                                    textTransform: 'none',
                                    fontSize: '20px',
                                    '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' }
                                }}
                            >
                                Choose file
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                        </Box>

                        {file && (
                            <Typography sx={{ fontSize: '14px', color: '#666', textAlign: 'center' }}>
                                Selected: {file.name}
                            </Typography>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
                        )}
                    </Box>
                )}

                {/* ---- LOADING STATE ---- */}
                {loading && (
                    <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <Typography variant="h6">{progressStatus}...</Typography>
                        <Box sx={{ width: '100%', px: 4 }}>
                            <LinearProgress
                                variant={progressTotal > 0 ? 'determinate' : 'indeterminate'}
                                value={progressPercent}
                                sx={{
                                    height: 10,
                                    borderRadius: 5,
                                    backgroundColor: '#E5E7EB',
                                    '& .MuiLinearProgress-bar': { backgroundColor: '#0063F7' }
                                }}
                            />
                        </Box>
                        {progressTotal > 0 && (
                            <Typography variant="body2" color="text.secondary">
                                {progressCurrent} of {progressTotal} rows processed
                            </Typography>
                        )}
                    </Box>
                )}

                {/* ---- RESULT STATE ---- */}
                {result && (
                    <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CheckCircleIcon sx={{ fontSize: 64, color: '#166534', mb: 1 }} />
                        <Typography
                            variant="h5"
                            sx={{
                                fontFamily: 'Roboto Slab',
                                fontWeight: 500,
                                color: '#166534',
                                mb: 1
                            }}
                        >
                            Import completed successfully
                        </Typography>

                        <Paper variant="outlined" sx={{ p: 3, width: '100%', backgroundColor: '#F0FDF4', borderColor: '#BBF7D0' }}>
                            <Stack spacing={1} sx={{ textAlign: 'left' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography fontWeight={500}>Total Rows in Sheet:</Typography>
                                    <Typography fontWeight={600}>{result.total || 0}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography fontWeight={500} color="#166534">Successfully Imported:</Typography>
                                    <Typography fontWeight={600} color="#166534">{result.success || 0}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography fontWeight={500} color="#92400E">Failed Rows:</Typography>
                                    <Typography fontWeight={600} color="#92400E">{result.failed || 0}</Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        {/* Show error details if any */}
                        {result.errors && result.errors.length > 0 && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    width: '100%',
                                    backgroundColor: '#FEF2F2',
                                    borderColor: '#FECACA',
                                    maxHeight: 200,
                                    overflow: 'auto'
                                }}
                            >
                                <Typography fontWeight={500} color="#991B1B" sx={{ mb: 1, textAlign: 'left' }}>
                                    Error Details:
                                </Typography>
                                {(() => {
                                    const duplicateErrors = result.errors.filter(err =>
                                        err.errors.some(e => e.toLowerCase().includes('already exists'))
                                    );
                                    const otherErrors = result.errors.filter(err =>
                                        !err.errors.some(e => e.toLowerCase().includes('already exists'))
                                    );

                                    return (
                                        <>
                                            {duplicateErrors.length > 0 && (
                                                <Typography sx={{ fontSize: '13px', color: '#991B1B', textAlign: 'left', mb: 1, fontWeight: 600 }}>
                                                    {duplicateErrors.length} policies already exist
                                                </Typography>
                                            )}
                                            {otherErrors.slice(0, 10).map((err, idx) => (
                                                <Typography
                                                    key={idx}
                                                    sx={{ fontSize: '13px', color: '#991B1B', textAlign: 'left', mb: 0.5 }}
                                                >
                                                    Row {err.row}: {err.errors.join(', ')}
                                                </Typography>
                                            ))}
                                            {otherErrors.length > 10 && (
                                                <Typography sx={{ fontSize: '13px', color: '#991B1B', textAlign: 'left', fontStyle: 'italic' }}>
                                                    ...and {otherErrors.length - 10} more errors
                                                </Typography>
                                            )}
                                        </>
                                    );
                                })()}
                            </Paper>
                        )}
                    </Box>
                )}
            </DialogContent>

            {/* ---- ACTIONS ---- */}
            {!loading && (
                <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'flex-end' }}>
                    {!result ? (
                        <>
                            <Button
                                onClick={handleClose}
                                sx={{
                                    width: '149px',
                                    height: '60px',
                                    borderRadius: '6px',
                                    borderColor: '#B00020',
                                    color: '#B00020',
                                    fontFamily: 'Roboto',
                                    fontWeight: 400,
                                    fontSize: '20px',
                                    lineHeight: '24px',
                                    letterSpacing: '0.01em',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: 'inherit',
                                        color: '#B00020',
                                        boxShadow: 10
                                    }
                                }}
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={!file}
                                variant="contained"
                                sx={{
                                    width: '149px',
                                    height: '60px',
                                    borderRadius: '6px',
                                    backgroundColor: '#0063F7 !important',
                                    fontFamily: 'Roboto',
                                    fontWeight: 400,
                                    fontSize: '20px',
                                    lineHeight: '24px',
                                    letterSpacing: '0.01em',
                                    textTransform: 'none',
                                    '&:disabled': {
                                        backgroundColor: '#CCCCCC !important',
                                        color: '#666666 !important',
                                    },
                                    '&:hover': {
                                        backgroundColor: '#0063F7 !important',
                                        color: '#FFFFFF !important',
                                        boxShadow: 10
                                    }
                                }}
                            >
                                Import
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                onClick={handleClose}
                                variant="outlined"
                                sx={{
                                    width: '149px',
                                    height: '60px',
                                    borderRadius: '6px',
                                    borderColor: '#B00020',
                                    color: '#B00020',
                                    fontFamily: 'Roboto',
                                    fontWeight: 400,
                                    fontSize: '20px',
                                    lineHeight: '24px',
                                    letterSpacing: '0.01em',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: 'inherit',
                                        color: '#B00020',
                                        boxShadow: 10
                                    }
                                }}
                            >
                                Close
                            </Button>
                            <Button
                                onClick={handleClose}
                                variant="contained"
                                sx={{
                                    width: '149px',
                                    height: '60px',
                                    borderRadius: '6px',
                                    backgroundColor: '#0063F7 !important',
                                    fontFamily: 'Roboto',
                                    fontWeight: 400,
                                    fontSize: '20px',
                                    lineHeight: '24px',
                                    letterSpacing: '0.01em',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#0063F7 !important',
                                        color: '#FFFFFF !important',
                                        boxShadow: 10
                                    }
                                }}
                            >
                                Done
                            </Button>
                        </>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default ImportPoliciesDialog;
